import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, Phone, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

// --- IMPOR DATABASE FASILITAS ---
import facilitiesDataRaw from '../data/facilities.json';
// Antisipasi jika import json menghasilkan objek bersarang (tergantung konfigurasi bundler)
const facilitiesList = Array.isArray(facilitiesDataRaw) ? facilitiesDataRaw : (facilitiesDataRaw?.default || []);

export default function FacilityDetail() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  
  const [facility, setFacility] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    const data = facilitiesList.find(f => f.slug === slug);
    setFacility(data || null);
    setPhotoIdx(0);
  }, [slug]);

  // LOGIKA SMART LANGUAGE UNTUK SEMUA TEXT
  const name = facility ? (lang === 'zh' ? facility.name_zh : (lang === 'en' ? facility.name_en : facility.name_id)) : 'Facility Detail';
  const tagline = facility ? (lang === 'zh' ? facility.tagline_zh : (lang === 'en' ? facility.tagline_en : facility.tagline_id)) : '';
  const description = facility ? (lang === 'zh' ? facility.desc_zh : (lang === 'en' ? facility.desc_en : facility.desc_id)) : '';
  const hours = facility ? (lang === 'zh' ? facility.hours_zh : (lang === 'en' ? facility.hours_en : facility.hours_id)) : '';
  const rules = facility ? (lang === 'zh' ? facility.rules_zh : (lang === 'en' ? facility.rules_en : facility.rules_id)) : [];
  
  usePageTitle(name);

  if (!facility) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6 pt-32">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="font-display text-2xl text-dark font-bold mb-2">
            {lang === 'id' ? 'Fasilitas tidak ditemukan' : lang === 'zh' ? '未找到设施' : 'Facility not found'}
          </h2>
          <Link to="/facilities" className="text-primary-600 font-sans text-sm hover:underline">
            ← {lang === 'id' ? 'Kembali ke Fasilitas' : lang === 'zh' ? '返回设施' : 'Back to Facilities'}
          </Link>
        </div>
      </div>
    );
  }

  const photos = facility.photos || [];
  const prev = () => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setPhotoIdx((i) => (i + 1) % photos.length);
  
  // Gambar Hero Background diambil dari foto pertama fasilitas (jika tidak ada gunakan warna abu fallback)
  const heroBgImage = photos.length > 0 ? photos[0].src : '';

  return (
    <div className="bg-cream min-h-screen">
      {/* ── HERO BANNER: Menggunakan Foto Asli sebagai Background ── */}
      <div 
        className="pt-32 pb-16 px-6 relative overflow-hidden bg-primary-950 bg-cover bg-center"
        style={{ backgroundImage: `url('${heroBgImage}')` }}
      >
        {/* Overlay Gelap agar teks mudah terbaca */}
        <div className="absolute inset-0 bg-black/70"></div>
        
        <div className="relative max-w-5xl mx-auto z-10">
          <button onClick={() => navigate('/facilities')} className="flex items-center gap-2 text-white/80 hover:text-white font-sans text-sm mb-6 transition-colors group">
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            {lang === 'id' ? 'Kembali ke Fasilitas' : lang === 'zh' ? '返回设施' : 'Back to Facilities'}
          </button>
          
          <div className="flex flex-col">
            <h1 className="font-display text-4xl md:text-5xl text-white font-bold">
              {name}
            </h1>
            <p className="text-white/80 font-body text-lg mt-3 max-w-2xl leading-relaxed">
              {tagline}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-14 space-y-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          
          {/* ── GALERI FOTO MURNI ── */}
          <div>
            {photos.length > 0 && (
              <>
                <div
                  className="relative h-80 rounded-2xl overflow-hidden cursor-pointer bg-gray-200"
                  onClick={() => setLightbox(true)}
                >
                  {/* Foto Utama Pembesar */}
                  <img 
                    src={photos[photoIdx].src || '/placeholder-image.jpg'} 
                    alt={name} 
                    className="w-full h-full object-cover" 
                  />
                  
                  {photos.length > 1 && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); prev(); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors">
                        <ChevronLeft size={18} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); next(); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors">
                        <ChevronRight size={18} />
                      </button>
                    </>
                  )}
                  
                  {/* Label Foto */}
                  <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs font-sans px-3 py-1.5 rounded-lg shadow-md backdrop-blur-sm">
                    {lang === 'zh' ? photos[photoIdx]?.label_zh : (lang === 'en' ? photos[photoIdx]?.label_en : photos[photoIdx]?.label_id)}
                  </div>
                  
                  {/* Indikator Angka */}
                  {photos.length > 1 && (
                    <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-sans px-2.5 py-1 rounded-lg shadow-md backdrop-blur-sm">
                      {photoIdx + 1} / {photos.length}
                    </div>
                  )}
                </div>
                
                {/* Thumbnail Bawah */}
                {photos.length > 1 && (
                  <div className="flex gap-2 mt-3">
                    {photos.map((p, i) => (
                      <button key={i} onClick={() => setPhotoIdx(i)}
                        className={`relative h-16 flex-1 rounded-xl overflow-hidden bg-gray-200 transition-all ${i === photoIdx ? 'ring-2 ring-primary-500 ring-offset-2 opacity-100' : 'opacity-50 hover:opacity-100'}`}>
                        <img src={p.src || '/placeholder-image.jpg'} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── KONTEN INFORMASI ── */}
          <div className="space-y-6">
            <div>
              <h2 className="font-display text-xl text-dark font-bold mb-3">
                {lang === 'id' ? 'Tentang Fasilitas Ini' : lang === 'zh' ? '关于此设施' : 'About This Facility'}
              </h2>
              <p className="text-gray-600 font-sans text-sm leading-relaxed">
                {description}
              </p>
            </div>
            
            <div className="bg-primary-50 border border-primary-100 rounded-xl p-4">
              <div className="flex items-center gap-2 text-primary-700 font-sans font-semibold text-sm mb-1">
                <Clock size={15} /> {lang === 'id' ? 'Jam Operasional' : lang === 'zh' ? '营业时间' : 'Operating Hours'}
              </div>
              <p className="text-primary-600 font-sans text-sm">
                {hours}
              </p>
            </div>
            
            <div>
              <h3 className="font-sans font-bold text-dark text-sm mb-3">
                {lang === 'id' ? '📋 Peraturan & Informasi' : lang === 'zh' ? '📋 规则与信息' : '📋 Rules & Information'}
              </h3>
              <ul className="space-y-2">
                {rules.map((rule, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-xs font-sans text-gray-600">
                    <span className="w-5 h-5 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold mt-0.5">
                      {i + 1}
                    </span>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
            
            <a 
              href={`https://wa.me/6281378575758?text=${encodeURIComponent(
                lang === 'id' ? `Halo Ndalem Mataram, saya ingin bertanya lebih lanjut mengenai fasilitas ${name}.` : 
                lang === 'zh' ? `你好 Ndalem Mataram，我想进一步咨询有关 ${name} 设施的信息。` : 
                `Hello Ndalem Mataram, I would like to ask further about the ${name} facility.`
              )}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-[#25D366] hover:bg-[#128C7E] text-white font-sans font-semibold rounded-xl transition-colors text-sm shadow-md hover:shadow-lg"
            >
              <Phone size={15} />
              {lang === 'id' ? 'Tanya via WhatsApp' : lang === 'zh' ? '通过 WhatsApp 咨询' : 'Ask via WhatsApp'}
            </a>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-8">
          <Link to="/facilities" className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-500 font-sans text-sm font-semibold transition-colors group">
            <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform" />
            {lang === 'id' ? 'Lihat Semua Fasilitas' : lang === 'zh' ? '查看所有设施' : 'View All Facilities'}
          </Link>
        </div>
      </div>

      {/* ── LIGHTBOX LAYAR PENUH ── */}
      {lightbox && photos[photoIdx] && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(false)}>
          <button className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors">
            <X size={20} />
          </button>
          <div className="relative w-full max-w-4xl h-[70vh] rounded-2xl overflow-hidden bg-black" onClick={(e) => e.stopPropagation()}>
            <img src={photos[photoIdx].src || '/placeholder-image.jpg'} alt="" className="w-full h-full object-contain" />
            
            {photos.length > 1 && (
              <>
                <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-black/90 rounded-full flex items-center justify-center text-white transition-colors">
                  <ChevronLeft size={24} />
                </button>
                <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-black/90 rounded-full flex items-center justify-center text-white transition-colors">
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm font-sans px-4 py-2 rounded-xl backdrop-blur-md">
               {lang === 'zh' ? photos[photoIdx]?.label_zh : (lang === 'en' ? photos[photoIdx]?.label_en : photos[photoIdx]?.label_id)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}